import { useState, useEffect } from "react";
import axios from "axios";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";
import enliveWordmarkwithIcon from "./Images/enliveWordmarkwithIcon.png"

// Components and pages
import Auth0Component from "./pages/Auth0Component";
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { MyProfile } from "./components/MyProfile";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import ThemeToggle from "./components/ThemeToggle";
import BackToTop from "./components/BackToTop";
import { NavBar } from "./components/NavBar";
import BetaGatePage from "./pages/BetaGatePage";
import { Workspace } from "./components/Workspace";
import "bootstrap-icons/font/bootstrap-icons.css";

// Import ModelManager for preloading
import MobileQwen3Manager, { ModelLoadProgress } from "./components/MobileQwen3Manager";
import Qwen3Manager from "./components/Qwen3Manager";
import { getLocalModelCapability } from "./components/hybrid_model_selection";

// ⭐ Import the UPDATED ProgressIndicator
import { ProgressIndicator } from "./components/ProgressIndicator";

// ⭐ NEW: Import the reload interceptor
import { initReloadInterceptor, forceRealReload } from "./components/reloadInterceptor";

// ⭐ NEW: Initialize the Navigation API reload interceptor at module level.
// This runs once when the module is first evaluated and persists across soft reloads.
initReloadInterceptor();
// ⭐ NEW: Module-level guard to prevent duplicate model initialization
let isModelInitStarted = false;

// ⭐ NEW: AppContent component that sits INSIDE Auth0Provider
function AppContent() {
  // ✅ NOW we can use useAuth0() because we're inside the Auth0Provider context
  const { isAuthenticated } = useAuth0();
  
  const [theme, setTheme] = useState<string>(
    localStorage.getItem("theme") || "light"
  );
  const [betaRank, setBetaRank] = useState<number | null>(null);
  const [isAuthenticatedLocal, setIsAuthenticated] = useState<boolean>(false);
  
  // Model loading state
  const [modelProgress, setModelProgress] = useState<ModelLoadProgress | null>(null);
  const [modelReady, setModelReady] = useState<boolean>(false);
  const [modelInfo, setModelInfo] = useState<string>('');

  // 1) Fetch BetaRank on mount
  useEffect(() => {
    axios
      .get("/getBetaRank", { withCredentials: true })
      .then((res) => {
        if (res.data && typeof res.data.betaRank === "number") {
          setBetaRank(res.data.betaRank);

          // ✅ Fire DHP in background if user is authenticated (betaRank === 1)
          if (res.data.betaRank === 1) {
            fetch('/trigger-dhp', {
              method: 'POST',
              credentials: 'include',
            }).catch(err => {
              console.warn('[DHP] Background trigger failed:', err);
            });
          }

        } else {
          setBetaRank(0); // Default to 0 if not found or invalid
        }
      })
      .catch((err) => {
        console.error("Error fetching BetaRank:", err);
        setBetaRank(0); // Default to 0 on error
      });
  }, []);

  // 2a) Theme setup
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);

    // Apply background color to html and body
    document.documentElement.style.backgroundColor =
      theme === "dark" ? "#23272a" : "#ffffff";
    document.body.style.backgroundColor =
      theme === "dark" ? "#23272a" : "#ffffff";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.minHeight = "100vh";
  }, [theme]);

  // 2b) ⭐ NEW: Listen for soft-reload events from the Navigation API interceptor
  useEffect(() => {
    const handleSoftReload = () => {
      const qwenManager = Qwen3Manager.getInstance();
      const mobileManager = MobileQwen3Manager.getInstance();
      const manager = qwenManager.isReady() ? qwenManager : mobileManager;
    
      if (manager.isReady()) {
        setModelReady(true);
        setModelProgress({
          loaded: 100, total: 100, percentage: 100,
          stage: 'ready',
          message: 'Model ready!',
        });
        const selectedModel = manager.getSelectedModel();
        if (selectedModel) {
          setModelInfo(`${selectedModel.name} (${(selectedModel.size / 1024 / 1024).toFixed(0)} MB)`);
        }
      }
      // Note: We intentionally do NOT clear conversation history here.
      // The user's conversation state is managed by child components
      // (e.g., ToolContainer) and persists across soft reloads,
      // which is the desired behavior — the user doesn't lose their work.
    };

    window.addEventListener('soft-reload', handleSoftReload);
    return () => {
      window.removeEventListener('soft-reload', handleSoftReload);
    };
  }, []); // Empty deps — register once, cleanup on unmount

  // 3a) Preload AI model after successful login (betaRank === 1)
  useEffect(() => {
    const preloadModel = async () => {
      // Only preload if user has access (betaRank === 1)
      if (betaRank !== 1) {
        console.log('⏭️ User does not have access, skipping model preload');
        return;
      }

      const capability = await getLocalModelCapability();
      console.log('[Local AI capability]', capability);

      if (!capability.supported) {
        console.warn('Local Enlive AI unsupported:', capability.reason);
        setModelReady(false);
        setModelProgress({
          loaded: 0,
          total: 100,
          percentage: 0,
          stage: 'checking',
          message: 'Local Enlive AI is not supported in this browser.',
        });
        return;
      }

      const manager = capability.runtime === 'qwen3'
        ? Qwen3Manager.getInstance()
        : MobileQwen3Manager.getInstance();

      if (manager.isReady() || isModelInitStarted) {
        console.log('✅ Model already loaded');
        setModelReady(true);

        const selectedModel = manager.getSelectedModel();
        if (selectedModel) {
          setModelInfo(`${selectedModel.name} (${(selectedModel.size / 1024 /
            1024).toFixed(0)} MB)`);
        }

        return;
      }
      
      // Set the guard immediately before any async work
      isModelInitStarted = true;

      try {
        const [,] = await Promise.all([
          axios.post("/reset-model-status", {}, { withCredentials: true }),
          Promise.resolve(),
        ]);
    
        console.log(`Starting background AI model preload (${capability.tier}: ${capability.modelId})...`);
    
        const unsubscribe = manager.onProgress((progress) => {
          setModelProgress(progress);
          if (progress.stage === 'ready') {
            setModelReady(true);
            const selectedModel = manager.getSelectedModel();
            if (selectedModel) {
              setModelInfo(`${selectedModel.name} (${(selectedModel.size / 1024 / 1024).toFixed(0)} MB)`);
            }
          }
        });
    
        const processor = await manager.getProcessor();
        console.log('✅ AI model loaded and ready!');
        unsubscribe();
    
      } catch (error) {
        isModelInitStarted = false;
        console.error('❌ Failed to preload AI model:', error);
        setModelProgress({
          loaded: 0, total: 100, percentage: 0,
          stage: 'checking',
          message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    };

    // Trigger preload when betaRank becomes 1
    if (betaRank === 1) {
      preloadModel();
    }
  }, [betaRank]); // Run when betaRank changes (after login)

  // 3b) Cleanup model on page unload (prevents WebGPU memory leak on refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      Qwen3Manager.getInstance().reset();
      MobileQwen3Manager.getInstance().reset();
      isModelInitStarted = false;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 4) If we haven't loaded BetaRank yet, show "Loading..."
  if (betaRank === null) {
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>Loading...</div>
    );
  }

  // Define root container style based on theme
  const rootContainerStyle = {
    minHeight: "100vh",
    display: "flex" as const,
    flexDirection: "column" as const,
    backgroundColor: theme === "dark" ? "#23272a" : "#ffffff",
  };

  return (
    <Router>
      <div data-bs-theme={theme} style={rootContainerStyle}>
        <div className="text-body" style={{ flex: "1 0 auto" }}>
          {/* Show NavBar only if betaRank===1 */}
          {betaRank === 1 && (
            <NavBar>
              {/* ⭐ NEW: Consolidated Progress Indicator in NavBar */}
              <ProgressIndicator 
                theme={theme} 
                modelProgress={modelProgress}
                modelReady={modelReady}
              />
            </NavBar>
          )}

          {/* Theme toggle */}
          <div className="position-absolute top-0 end-0 m-3">
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>

          {/* ⭐ REMOVED: Separate Mobile Qwen3 model loading indicator */}
          {/* The model loading is now consolidated into ProgressIndicator */}

          {/* Centered Enlive logo */}
          <div className="d-flex justify-content-center">
            <img 
              src={enliveWordmarkwithIcon} 
              alt="Enlive Logo" 
              className="enliveLogo" 
            />
          </div>

          <Routes>
            <Route
              path="/"
              element={
                betaRank === 1 ? (
                  <Workspace
                    isDark={theme === "dark"}
                    modelProgress={modelProgress}
                    modelReady={modelReady}
                  />
                ) : (
                  <BetaGatePage onSuccessfulReferral={() => setBetaRank(1)} />
                )
              }
            />

            <Route
              path="/login"
              element={
                <Auth0Component setIsAuthenticated={setIsAuthenticated} />
              }
            />
            <Route
              path="/callback"
              element={
                <Auth0Component setIsAuthenticated={setIsAuthenticated} />
              }
            />

            <Route
              path="/my-profile"
              element={<MyProfile isDark={theme === "dark"} />}
            />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
            <Route path="/termsofuse" element={<TermsOfUse />} />
          </Routes>

        </div>

        <BackToTop />
      </div>

      {/* Add CSS for loading spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Router>
  );
}

// ⭐ Main App component - just wraps AppContent with Auth0Provider
function App() {
  return (
    <Auth0Provider
      domain="auth.enlive.inc"
      clientId="CYt9ucrt4XYIGKO8Q2bMlfBD2VK0epEV"
      authorizationParams={{
        redirect_uri: window.location.origin + "/callback"
      }}
    >
      <AppContent />
    </Auth0Provider>
  );
}

export default App;